import { useMemo, useState } from "react";
import clsx from "clsx";
import { useCameraStatus } from "../hooks/useCameraStatus";
import { useNow } from "../hooks/useNow";
import { cameraStyle } from "../lib/cameraStatusStyles";
import { relativeTime } from "../lib/relativeTime";
import AppHeader from "../components/AppHeader";
import PageTabs from "../components/PageTabs";
import RefreshIndicator from "../components/RefreshIndicator";
import { InfoTip, SegToggle } from "../components/history/HistoryPrimitives";
import Tip from "../components/Tip";
import type {
  CameraStatus,
  CameraStatusResponse,
  DailyPoint,
  SiteStatus,
} from "../types/cameraApi";

/** Skeleton card shown while the first /api/camera/status response is in flight. */
function CameraCardSkeleton() {
  return (
    <div
      className="rounded-2xl border border-surface-border bg-surface p-4 shadow-sm dark:border-surface-darkBorder dark:bg-surface-darkSubtle"
      aria-hidden="true"
    >
      <div className="flex items-center justify-between">
        <div className="h-4 w-28 animate-pulse rounded bg-slate-200 dark:bg-slate-800" />
        <div className="h-5 w-16 animate-pulse rounded-full bg-slate-200 dark:bg-slate-800" />
      </div>
      <div className="mt-4 h-4 w-20 animate-pulse rounded bg-slate-100 dark:bg-slate-800/70" />
      <div className="mt-4 h-2.5 w-full animate-pulse rounded bg-slate-100 dark:bg-slate-800/70" />
    </div>
  );
}

/**
 * Pill badge for a camera shift status — colored dot + Korean label.
 * Color is never the sole signal (a11y): the label is always rendered.
 */
function CameraStatusBadge({ status }: { status: CameraStatus["status"] }) {
  const style = cameraStyle(status);
  return (
    <span
      role="status"
      aria-label={style.label}
      className={clsx(
        "inline-flex items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium",
        style.bg,
        style.fg,
        style.border,
      )}
    >
      <span
        aria-hidden="true"
        className={clsx("inline-block h-2 w-2 rounded-full", style.solid)}
      />
      <span>{style.label}</span>
    </span>
  );
}

/** 이력 기간 프리셋(일). 기본 30일만 한눈에, 필요 시 90일(백엔드 보관 윈도우)까지 펼친다. */
const HISTORY_PERIOD_OPTIONS: { value: number; label: string }[] = [
  { value: 30, label: "30일" },
  { value: 60, label: "60일" },
  { value: 90, label: "90일" },
];
const HISTORY_DEFAULT_DAYS = 30;

/** '카메라 화각 상태' 도움말 — status 색/거리·임계/이력바/갱신 주기 설명. */
const CAMERA_INFO = {
  summary:
    "각 카메라의 현재 화각(시야)이 기준 프레임에서 얼마나 틀어졌는지 보여줍니다.",
  basis:
    "매일 1회, 기준 프레임과 현재 프레임의 특징점을 매칭해 이동 거리(px)를 구하고 임계값(기본 30px)과 비교해 판정합니다.",
  rows: [
    {
      term: "12.3 / 30px",
      desc: "현재 이동 거리 / 임계값. 거리가 임계를 넘으면 '화각 틀어짐'",
    },
    {
      term: "이력바",
      desc: "막대 1개 = 하루, 색 = 그날 상태. 누르거나 마우스를 올리면 날짜·상태·거리(px)",
    },
  ],
  legend: [
    { swatch: "bg-status-operational", label: "초록 — 정상 (임계 이내)" },
    { swatch: "bg-status-majorOutage", label: "빨강 — 화각 틀어짐 (임계 초과)" },
    { swatch: "bg-status-degraded", label: "노랑 — 기준 설정 (기준 프레임 수집 중)" },
    { swatch: "bg-status-unknown", label: "회색 — 점검 필요 (분석 실패)" },
    { swatch: "bg-slate-200", label: "옅은 회색 — 기록 없음 (그날 점검 데이터 없음)" },
  ],
  period: "이력바 기간은 우측에서 선택(30/60/90일) · 갱신 일 1회",
};

/** 최근 n일의 ISO date(YYYY-MM-DD) 배열을 오래된→최신 순으로. KST 로컬 기준(`/history` 와 동일). */
function lastNDays(n: number): string[] {
  const out: string[] = [];
  const today = new Date();
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    out.push(`${y}-${m}-${day}`);
  }
  return out;
}

/**
 * 이력바 한 칸 — `/history` UptimeSlot 스타일. 데이터 있는 날은 status 색, 없는 날은 회색
 * placeholder. 탭/hover/포커스 시 그날 상세(날짜+시간 · 상태 · 거리)를 popover 로 노출한다.
 */
function HistorySlot({ point, day }: { point: DailyPoint | null; day: string }) {
  // checkedAt(ISO `+09:00`)에서 KST 시:분만 잘라 표시 — 타임존 변환 없이 점검 시각 그대로.
  const time = point?.checkedAt ? point.checkedAt.slice(11, 16) : "";
  const style = point ? cameraStyle(point.status) : null;
  const title = point ? `${point.day}${time ? ` ${time}` : ""}` : day;
  const body = point
    ? `${style!.label} · ${point.distance.toFixed(1)}px`
    : "기록 없음";
  return (
    <Tip
      ariaLabel={`${title} · ${body}`}
      placement="top"
      align="center"
      panelClassName="whitespace-nowrap"
      wrapperClassName="flex-1"
      className={clsx(
        "h-6 w-full rounded-sm transition-transform",
        "hover:scale-y-110 focus:outline-none focus-visible:ring-2 focus-visible:ring-slate-400",
        style ? style.solid : "bg-slate-200 dark:bg-slate-700",
      )}
      content={
        <>
          <span className="block font-semibold tabular-nums">{title}</span>
          <span className="block text-slate-200">{body}</span>
        </>
      }
    >
      <span className="sr-only">{title}</span>
    </Tip>
  );
}

/**
 * 미니 이력바 — `/history` 페이지와 동일한 일별 슬롯 + hover popover 스타일.
 * 선택된 기간({@code windowIsoDays}, 오래된→최신)만큼 슬롯을 그리며, 데이터 없는 날은
 * 회색 placeholder 로 채운다. 기간은 상위에서 한 번 계산해 모든 카드에 공유한다.
 */
function HistoryBar({
  history,
  windowIsoDays,
}: {
  history: DailyPoint[];
  windowIsoDays: string[];
}) {
  const byDay = new Map(history.map((p) => [p.day, p]));
  return (
    <div
      className="flex h-6 items-end gap-[1px]"
      role="img"
      aria-label={`최근 ${windowIsoDays.length}일 화각 이력`}
    >
      {windowIsoDays.map((iso) => (
        <HistorySlot key={iso} day={iso} point={byDay.get(iso) ?? null} />
      ))}
    </div>
  );
}

/** 카메라 카드 — cameraId, status 배지, distance/threshold, 선택 기간 미니 이력바. */
function CameraCard({
  camera,
  thresholdPx,
  windowIsoDays,
}: {
  camera: CameraStatus;
  thresholdPx: number;
  windowIsoDays: string[];
}) {
  // 선택된 기간 안에 실제 기록이 있는 날 수(라벨 "기록 N일"). windowIsoDays[0] = 기간 시작일.
  const windowFirst = windowIsoDays[0];
  const recordCount = camera.history.reduce(
    (n, p) => (p.day >= windowFirst ? n + 1 : n),
    0,
  );
  return (
    <article
      className={clsx(
        "rounded-2xl border bg-surface p-4 shadow-sm",
        "border-surface-border dark:border-surface-darkBorder",
        "dark:bg-surface-darkSubtle",
      )}
      aria-label={`${camera.cameraId} 화각 상태`}
    >
      <header className="flex items-start justify-between gap-3">
        <h4 className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {camera.cameraId}
        </h4>
        <CameraStatusBadge status={camera.status} />
      </header>

      <div className="mt-3 flex items-baseline gap-1 tabular-nums">
        <span className="text-lg font-semibold text-slate-900 dark:text-slate-100">
          {camera.distance.toFixed(1)}
        </span>
        <span className="text-xs text-slate-500 dark:text-slate-400">
          / {thresholdPx}px
        </span>
      </div>

      <div className="mt-3">
        <HistoryBar history={camera.history} windowIsoDays={windowIsoDays} />
        <div className="mt-1 text-[11px] text-slate-400 dark:text-slate-500">
          최근 {windowIsoDays.length}일 · 기록 {recordCount}일
        </div>
      </div>
    </article>
  );
}

/** 사이트 섹션 — siteName(siteId 작게) + 카메라 카드 그리드. */
function SiteSection({
  site,
  thresholdPx,
  windowIsoDays,
}: {
  site: SiteStatus;
  thresholdPx: number;
  windowIsoDays: string[];
}) {
  const now = useNow();
  return (
    <section aria-label={`${site.siteName} 카메라`} className="mt-8 first:mt-0">
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <h3 className="text-base font-semibold text-slate-900 dark:text-slate-100">
          {site.siteName}
        </h3>
        <span className="text-xs text-slate-400 tabular-nums dark:text-slate-500">
          {site.siteId}
        </span>
        <span
          className="text-xs text-slate-500 tabular-nums dark:text-slate-400"
          title={site.checkedAt}
        >
          점검 {relativeTime(site.checkedAt, { referenceNow: now })}
        </span>
      </div>

      <div className="mt-3 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {site.cameras.map((camera) => (
          <CameraCard
            key={camera.cameraId}
            camera={camera}
            thresholdPx={thresholdPx}
            windowIsoDays={windowIsoDays}
          />
        ))}
      </div>
    </section>
  );
}

/**
 * 전체 요약 헤더 배너 — DETECTED 카메라가 1건 이상이면 적색 경고, 없으면 녹색 정상.
 * 마지막 점검 상대시간은 가장 최신 checkedAt 기준.
 */
function SummaryBanner({
  detectedCount,
  latestCheckedAt,
}: {
  detectedCount: number;
  latestCheckedAt: string | null;
}) {
  const now = useNow();
  const hasDetected = detectedCount > 0;
  const tone = hasDetected ? cameraStyle("DETECTED") : cameraStyle("NORMAL");

  return (
    <div
      role={hasDetected ? "alert" : "status"}
      className={clsx(
        "rounded-2xl border px-5 py-4 sm:px-6 sm:py-5",
        tone.bg,
        tone.fg,
        tone.border,
      )}
    >
      <div className="flex items-center gap-2.5">
        <span
          aria-hidden="true"
          className={clsx("inline-block h-3 w-3 rounded-full", tone.solid)}
        />
        <h2 className="text-base font-semibold sm:text-lg">
          {hasDetected
            ? `화각 틀어짐 ${detectedCount}건 감지`
            : "모든 카메라 화각 정상"}
        </h2>
      </div>
      <p className="mt-1 text-xs sm:text-sm">
        {hasDetected
          ? "기준 프레임 대비 화각이 임계값을 초과한 카메라가 있습니다. 아래에서 확인하세요."
          : "기준 프레임 대비 모든 카메라의 화각 이동이 임계값 이내입니다."}
        {latestCheckedAt
          ? ` · 마지막 점검 ${relativeTime(latestCheckedAt, { referenceNow: now })}`
          : ""}
      </p>
    </div>
  );
}

/**
 * 카메라 화각(FOV) 변화 감지 페이지.
 *
 * Data flow:
 *   useCameraStatus() ──┬── DETECTED 집계 + 최신 checkedAt ──► SummaryBanner
 *                       └── sites[] ──► SiteSection × N ──► CameraCard × M
 *
 * Error handling: 초기 에러(데이터 없음)만 전체 에러 블록을 표시한다. 일시적 재조회
 * 실패는 placeholderData 로 마지막 성공 데이터를 유지하므로 무시한다(StatusPage 동일).
 */
export default function CameraPage() {
  const { data, error, isLoading, isFetching, dataUpdatedAt, refetch } =
    useCameraStatus();

  const status: CameraStatusResponse | undefined = data;
  const hasInitialError = !status && !!error;
  const thresholdPx = status?.thresholdPx ?? 30;

  // 이력 기간(일) — 기본 30일. 모든 카드 공유 윈도우를 한 번만 계산한다.
  const [historyDays, setHistoryDays] = useState(HISTORY_DEFAULT_DAYS);
  const windowIsoDays = useMemo(() => lastNDays(historyDays), [historyDays]);

  // DETECTED 카메라 총합 + 가장 최신 checkedAt — 전체 응답 기준(필터 영향 없음).
  const { detectedCount, latestCheckedAt } = useMemo(() => {
    if (!status) return { detectedCount: 0, latestCheckedAt: null as string | null };
    let detected = 0;
    let latest: number | null = null;
    let latestStr: string | null = null;
    for (const site of status.sites) {
      const t = new Date(site.checkedAt).getTime();
      if (!Number.isNaN(t) && (latest == null || t > latest)) {
        latest = t;
        latestStr = site.checkedAt;
      }
      for (const cam of site.cameras) {
        if (cam.status === "DETECTED") detected += 1;
      }
    }
    return { detectedCount: detected, latestCheckedAt: latestStr };
  }, [status]);

  const isEmpty = !!status && status.sites.length === 0;

  return (
    <div className="min-h-full bg-slate-50 text-slate-900 dark:bg-surface-dark dark:text-slate-100">
      <AppHeader
        right={
          <RefreshIndicator
            dataUpdatedAt={dataUpdatedAt}
            isFetching={isFetching}
            refetchIntervalMs={5 * 60_000}
            onRefresh={() => {
              void refetch();
            }}
          />
        }
      />

      <main className="mx-auto max-w-5xl px-4 py-6 sm:px-6 sm:py-12">
        <PageTabs />

        {hasInitialError ? (
          <div
            role="alert"
            className="rounded-2xl border border-status-majorOutage/30 bg-status-majorOutage-bg px-6 py-6 text-status-majorOutage-fg dark:border-status-majorOutage-dark/30 dark:bg-status-majorOutage-darkBg dark:text-status-majorOutage-darkFg"
          >
            <h2 className="text-base font-semibold">
              화각 정보를 불러오지 못했습니다
            </h2>
            <p className="mt-1 text-sm">
              서버에 도달할 수 없습니다. 잠시 후 다시 시도해주세요.
            </p>
          </div>
        ) : isLoading || !status ? (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <CameraCardSkeleton />
            <CameraCardSkeleton />
            <CameraCardSkeleton />
          </div>
        ) : isEmpty ? (
          <>
            <SummaryBanner
              detectedCount={0}
              latestCheckedAt={latestCheckedAt}
            />
            <div className="mt-6 rounded-2xl border border-surface-border bg-surface px-6 py-12 text-center text-sm text-slate-500 dark:border-surface-darkBorder dark:bg-surface-darkSubtle dark:text-slate-400">
              아직 수신된 화각 데이터가 없습니다.
            </div>
          </>
        ) : (
          <>
            <SummaryBanner
              detectedCount={detectedCount}
              latestCheckedAt={latestCheckedAt}
            />
            <div className="mt-6 flex flex-wrap items-center justify-between gap-2">
              <span className="flex items-center gap-1.5 text-xs font-medium text-slate-600 dark:text-slate-300">
                카메라 화각 상태
                <InfoTip content={CAMERA_INFO} />
              </span>
              <div className="flex items-center gap-2">
                <span className="text-xs text-slate-500 dark:text-slate-400">
                  이력 기간
                </span>
                <SegToggle
                  value={historyDays}
                  onChange={setHistoryDays}
                  options={HISTORY_PERIOD_OPTIONS}
                  ariaLabel="이력 기간 선택"
                />
              </div>
            </div>
            <div className="mt-3">
              {status.sites.map((site) => (
                <SiteSection
                  key={site.siteId}
                  site={site}
                  thresholdPx={thresholdPx}
                  windowIsoDays={windowIsoDays}
                />
              ))}
            </div>
          </>
        )}

        <footer className="mt-10 flex flex-col gap-1 border-t border-surface-border pt-6 text-xs text-slate-500 dark:border-surface-darkBorder dark:text-slate-400 sm:flex-row sm:items-center sm:justify-between">
          <div className="tabular-nums">
            화각 임계값 {thresholdPx}px · 일 1회 갱신
          </div>
          <div>vestellalab · bridgeChecker</div>
        </footer>
      </main>
    </div>
  );
}
